import { stringify } from 'qs';
import { AxiosError } from 'axios';
import i18next from '../../../lib/i18n';
import IApiResponse from '../../interfaces/IApiResponse';
import IPagination from '../../interfaces/IPagination';
import { getLoadManagerAxiosInstance } from '../axios-config';
import ILoadListData from '../../interfaces/loads/ILoadListData';
import ILoadsFiltersModel from '../../interfaces/IFiltersModel';
import ILoadsListRequest from '../../interfaces/loads/ILoadsListRequest';
import IApiError from '../../errors/interfaces/IApiError';
import ProcessingError from '../../errors/processing-error';
import ILoadSuspensionRequest from '../../interfaces/loads/ILoadSuspensionRequest';
import ILoadReOfferReturnData from '../../interfaces/loads/ILoadReOfferReturnData';
import LoadsFilterLocationType from '../../enums/loads/loads-filter-tags-location-type.enum';
import ILoadsLocationModel from '../../interfaces/loads/ILoadsLocationModel';
import ILoadsLocationResponse from '../../interfaces/loads/ILoadsLocationResponse';
import ILoadMaterialResponse from '../../interfaces/loads/ILoadMaterialResponse';
import ILoadPrioritizationResponse from '../../interfaces/loads/ILoadPrioritizationResponse';
import LoadsConstants from '../../../pages/business/loads/constants';
import ISelectInputModel from '../../components/load-filters/load-filter-advanced/components/location-filter/interfaces/ISelectInputModel';

const getLoads = async (
    filters?: ILoadsFiltersModel,
    page: number = 1,
    pageSize: number = LoadsConstants.PAGE_SIZE,
): Promise<IApiResponse<IPagination<ILoadListData>>> => {
    const request: ILoadsListRequest = getLoadsRequest(page, pageSize, filters);
    const Api = await getLoadManagerAxiosInstance();

    try {
        const response = await Api.get(`v1/loads:paginated`, {
            params: request,
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        return {
            response: (response.data || {}) as IPagination<ILoadListData>,
        };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

const suspendLoads = async (
    loadSuspensions: ILoadSuspensionRequest[],
): Promise<IApiResponse<string>> => {
    const api = await getLoadManagerAxiosInstance();

    try {
        const response = await api.post('v1/loads/suspend', loadSuspensions);

        return response.data;
    } catch (err: any) {
        const error = err as AxiosError<IApiError>;

        if (error?.response?.data && Array.isArray(error?.response?.data)) {
            return { error: new ProcessingError(error.response.data[0].Error) };
        }

        return {
            error: new ProcessingError(i18next.t('LoadErroTentarSuspender')),
        };
    }
};

const reOfferLoads = async (
    loadsIds: string[],
): Promise<IApiResponse<ILoadReOfferReturnData>> => {
    const api = await getLoadManagerAxiosInstance();

    try {
        const response = await api.post('/v1/loads/reoffer', loadsIds);

        return { response: (response.data || {}) as ILoadReOfferReturnData };
    } catch {
        return { error: new Error(i18next.t('LoadErroTentarReofertar')) };
    }
};

const getLoadsLocation = async (
    filter?: ILoadsLocationModel,
): Promise<IApiResponse<ILoadsLocationResponse[]>> => {
    const api = await getLoadManagerAxiosInstance();

    const request: ILoadsLocationModel = {
        search: filter?.search,
        searchFederativeUnit: filter?.searchFederativeUnit,
        locationType: filter?.locationType ?? LoadsFilterLocationType.Source,
        _page: filter?._page ?? 1,
        _size: filter?._size ?? 30,
    };

    try {
        const response = await api.get(`v1/loads/locations:paginated`, {
            params: request,
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        const result = (response.data ||
            {}) as IPagination<ILoadsLocationResponse>;

        return { response: result.data };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

const getLoadsLocationStates = async (
    locationType: LoadsFilterLocationType,
): Promise<IApiResponse<string[]>> => {
    const api = await getLoadManagerAxiosInstance();

    try {
        const response = await api.get(`v1/loads/locations/states`, {
            params: {
                locationType,
            },
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        return { response: (response.data || []) as string[] };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultarEstados')) };
    }
};

const getLoadsMaterial = async (
    materialName?: string,
    page: number = 1,
    pageSize: number = 10,
): Promise<IApiResponse<ILoadMaterialResponse[]>> => {
    const api = await getLoadManagerAxiosInstance();

    const request = {
        name: materialName,
        _page: page,
        _size: pageSize,
    };

    try {
        const response = await api.get(`v1/loads/material:paginated`, {
            params: request,
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        const result: IPagination<ILoadMaterialResponse> = response.data || {};

        return { response: result.data };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

const prioritizeLoads = async (
    loadIds: string[],
): Promise<IApiResponse<ILoadPrioritizationResponse>> => {
    const api = await getLoadManagerAxiosInstance();

    try {
        const response = await api.post(`v1/loads/prioritize`, { loadIds });

        return {
            response: (response.data || {}) as ILoadPrioritizationResponse,
        };
    } catch (err: any) {
        const error = err as AxiosError<IApiError>;

        if (error?.response?.data && Array.isArray(error?.response?.data)) {
            return { error: new ProcessingError(error.response.data[0].Error) };
        }

        return {
            error: new ProcessingError(i18next.t('LoadErroTentarPriorizar')),
        };
    }
};

function getLoadsRequest(
    page: number,
    pageSize: number,
    filters?: ILoadsFiltersModel,
): ILoadsListRequest {
    return {
        _page: page,
        _size: pageSize,
        loadId: filters?.loadId,
        loadStatus: filters?.loadStatus,
        channels: filters?.channels,
        initialCollectionDate: filters?.initialCollectionDate,
        endingCollectionDate: filters?.endingCollectionDate,
        originFederativeUnit: filters?.originFederativeUnit,
        originLocations: filters?.originLocations,
        destinationFederativeUnit: filters?.destinationFederativeUnit,
        destinationLocations: filters?.destinationLocations,
        materialId: filters?.product,
        loadCategories: filters?.loadCategories,
    } as ILoadsListRequest;
}

const getPoolLocations = async (
    filter?: ILoadsLocationModel,
): Promise<IApiResponse<ILoadsLocationResponse[]>> => {
    const Api = await getLoadManagerAxiosInstance();

    const request: ILoadsLocationModel = {
        search: filter?.search,
        searchFederativeUnit: filter?.searchFederativeUnit,
        locationType: filter?.locationType ?? LoadsFilterLocationType.Source,
        _page: filter?._page ?? 1,
        _size: filter?._size ?? 100,
    };

    try {
        const response = await Api.get(`v1/pool/locations`, {
            params: request,
            paramsSerializer(params) {
                return stringify(params, { allowDots: true });
            },
        });

        return {
            response: (response.data?.data || []) as ILoadsLocationResponse[],
        };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

const getPoolMaterials = async (
    name?: string,
    page: number = LoadsConstants.INITIAL_PAGE_NUMBER,
    pageSize: number = 100, // Increased for pool filters
): Promise<IApiResponse<ILoadMaterialResponse[]>> => {
    const Api = await getLoadManagerAxiosInstance();

    try {
        const response = await Api.get(`v1/pool/materials`, {
            params: {
                Search: name,
                _page: page,
                _size: pageSize,
            },
        });

        return {
            response: (response.data?.data || []) as ILoadMaterialResponse[],
        };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

// Helper functions for pool filter values
const fetchPoolSourceLocationsFilterValues = async (): Promise<
    IApiResponse<ISelectInputModel[]>
> => {
    try {
        const { response: locationsResults } = await getPoolLocations({
            locationType: LoadsFilterLocationType.Source,
        } as ILoadsLocationModel);

        const sourceOptions: ISelectInputModel[] = [];

        if (locationsResults) {
            locationsResults.forEach((location, index) => {
                sourceOptions.push({
                    key: index.toString(),
                    label: `${location.description} (${location.code})`,
                    value: location.code,
                });
            });
        }

        return { response: sourceOptions };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

const fetchPoolDestinationLocationsFilterValues = async (): Promise<
    IApiResponse<ISelectInputModel[]>
> => {
    try {
        const { response: locationsResults } = await getPoolLocations({
            locationType: LoadsFilterLocationType.Destination,
        } as ILoadsLocationModel);

        const destinationOptions: ISelectInputModel[] = [];

        if (locationsResults) {
            locationsResults.forEach((location, index) => {
                destinationOptions.push({
                    key: index.toString(),
                    label: `${location.description} (${location.code})`,
                    value: location.code,
                });
            });
        }

        return { response: destinationOptions };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

const fetchPoolMaterialsFilterValues = async (): Promise<
    IApiResponse<ISelectInputModel[]>
> => {
    try {
        const { response } = await getPoolMaterials(
            undefined,
            LoadsConstants.INITIAL_PAGE_NUMBER,
            100,
        );
        const materialOptions: ISelectInputModel[] = [];

        if (response) {
            response.forEach(material => {
                materialOptions.push({
                    key: material.id?.toString() || '',
                    label: material.name,
                    value: material.id,
                });
            });
        }

        return { response: materialOptions };
    } catch {
        return { error: new Error(i18next.t('CargasErroConsultar')) };
    }
};

const LoadsService = {
    getLoads,
    suspendLoads,
    reOfferLoads,
    getLoadsLocation,
    getLoadsLocationStates,
    getLoadsMaterial,
    prioritizeLoads,
    getPoolLocations,
    getPoolMaterials,
    fetchPoolSourceLocationsFilterValues,
    fetchPoolDestinationLocationsFilterValues,
    fetchPoolMaterialsFilterValues,
};

export default LoadsService;
